import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, ShoppingCart, CheckCircle, AlertTriangle, Play, RotateCcw, Bug } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { ModuleLayout } from '../../ui/ModuleLayout';

// ============ TYPES & DATA ============
type Operator = '>' | '<' | '===' | '!==' | '>=' | '<=' | '==';
type ReturnValue = 'Out of Stock' | 'In Stock' | 'Low Stock' | 'Unknown' | 'Overstock';

interface Level {
    id: number;
    title: string;
    description: string;
    customerComplaint: string;
    initialState: {
        op1: Operator;
        val1: number | string;
        ret1: ReturnValue;
    };
    correctState: {
        op1: Operator;
        val1: number | string;
        ret1: ReturnValue;
    };
    stockCases: (number | string | null)[];
}

const LEVELS: Level[] = [
    {
        id: 1,
        title: "The Zero Bug",
        description: "The system is confused. It thinks we are out of stock when we have items!",
        customerComplaint: "My screen says 'Out of Stock' but I see 50 bananas on the shelf! 🍌",
        initialState: { op1: '>', val1: 0, ret1: 'Out of Stock' },
        correctState: { op1: '===', val1: 0, ret1: 'Out of Stock' },
        stockCases: [0, 5, 20]
    },
    {
        id: 2,
        title: "The Late Warning",
        description: "We are ordering too late! We need to warn when stock is below the threshold.",
        customerComplaint: "We ran out of bananas because the 'Low Stock' alert didn't trigger until we had 2 left!",
        initialState: { op1: '<', val1: 2, ret1: 'Low Stock' },
        correctState: { op1: '<', val1: 'threshold', ret1: 'Low Stock' },
        stockCases: [0, 8, 15] // Assuming threshold is 10
    },
    {
        id: 3,
        title: "The Default State",
        description: "If it's not empty and not low, it should be 'In Stock'.",
        customerComplaint: "The system just says 'Unknown' for a full shelf. Customers are scared to buy.",
        initialState: { op1: '>', val1: 0, ret1: 'Unknown' },
        correctState: { op1: '>', val1: 0, ret1: 'In Stock' },
        stockCases: [20, 50, 100]
    },
    {
        id: 4,
        title: "Precise Counting",
        description: "We shouldn't trigger alerts if we have EXACTLY the threshold amount.",
        customerComplaint: "The system says 'Low Stock' when we have exactly 10 items. That's annoying.",
        initialState: { op1: '<=', val1: 'threshold', ret1: 'Low Stock' },
        correctState: { op1: '<', val1: 'threshold', ret1: 'Low Stock' },
        stockCases: [9, 10, 11]
    },
    {
        id: 5,
        title: "Negative Reality",
        description: "Somehow, the stock count dropped to -5. That shouldn't start a reorder, it's an error!",
        customerComplaint: "The inventory system ordered 500 bananas because it thought -5 was 'Low Stock'.",
        initialState: { op1: '<', val1: 0, ret1: 'Low Stock' },
        correctState: { op1: '<', val1: 0, ret1: 'Unknown' }, // Treat negative as error/unknown
        stockCases: [-5, 0, 5]
    },
    {
        id: 6,
        title: "Overstock Crisis",
        description: "If we have too many items, we need a special 'Overstock' tag.",
        customerComplaint: "The bananas are spilling into the aisles! We have 200 items in a 50 item shelf.",
        initialState: { op1: '>', val1: 0, ret1: 'In Stock' },
        correctState: { op1: '>', val1: 100, ret1: 'Overstock' },
        stockCases: [50, 100, 150]
    },
    {
        id: 7,
        title: "Strictly Business",
        description: "Only use strict equality (===) to avoid type cohesion errors.",
        customerComplaint: "The system matched '0' (string) with 0 (number) and caused a glitch.",
        initialState: { op1: '==', val1: 0, ret1: 'Out of Stock' },
        correctState: { op1: '===', val1: 0, ret1: 'Out of Stock' },
        stockCases: [0, '0', 1] // '0' simulates string from API
    },
    {
        id: 8,
        title: "The Ghost Item",
        description: "Sometimes the sensor reads 'null'. That is NOT zero.",
        customerComplaint: "Sensor broke (null), but system said 'Out of Stock' and ordered more.",
        initialState: { op1: '===', val1: 0, ret1: 'Out of Stock' },
        correctState: { op1: '===', val1: 'null', ret1: 'Unknown' },
        stockCases: [0, null, 5]
    },
    {
        id: 9,
        title: "Flash Sale Logic",
        description: "During a sale, the Low Stock threshold should be higher (20).",
        customerComplaint: "Sale started, we sold out in 5 minutes. Threshold needs to adapt.",
        initialState: { op1: '<', val1: 'threshold', ret1: 'Low Stock' },
        correctState: { op1: '<', val1: 20, ret1: 'Low Stock' },
        stockCases: [15, 25, 30]
    },
    {
        id: 10,
        title: "System Stabilization",
        description: "Final check. Ensure 0 is Out, Low is Low, and High is In.",
        customerComplaint: "One final verify before we go live globally.",
        initialState: { op1: '>', val1: 0, ret1: 'Unknown' },
        correctState: { op1: '>', val1: 0, ret1: 'In Stock' },
        stockCases: [0, 5, 50]
    }
];

export const InventoryLogic = () => {
    const completeModule = useAcademyStore(state => state.completeModule);
    const [currentLevelIdx, setCurrentLevelIdx] = useState(0);
    const [completed, setCompleted] = useState(false);

    // Game State
    const [operator, setOperator] = useState<Operator>('>');
    const [value, setValue] = useState<string | number>(0);
    const [returnValue, setReturnValue] = useState<ReturnValue>('Out of Stock');

    const [isRunning, setIsRunning] = useState(false);
    const [testResults, setTestResults] = useState<{ stock: number, result: string, passed: boolean }[]>([]);
    const [feedback, setFeedback] = useState<string | null>(null);

    const level = LEVELS[currentLevelIdx];

    // Initialize state when level changes
    useEffect(() => {
        setOperator(level.initialState.op1);
        setValue(level.initialState.val1);
        setReturnValue(level.initialState.ret1);
        setTestResults([]);
        setFeedback(null);
    }, [currentLevelIdx]);

    const runTests = () => {
        setIsRunning(true);
        setFeedback(null);
        setTestResults([]);

        const results: { stock: number, result: string, passed: boolean }[] = [];
        let allPassed = true;

        // Animate through cases
        level.stockCases.forEach((stock: any, i) => {
            setTimeout(() => {
                // Simulate Logic
                let result = '';

                // Logic Interpreter based on Level Context

                // Special case for Level 6 (Overstock) logic check
                if (level.id === 6 && stock > 100 && returnValue === 'Overstock' && operator === '>' && Number(value) === 100) {
                    result = 'Overstock';
                }
                // Special case for Level 5 (Negative)
                else if (level.id === 5 && stock < 0) {
                    result = returnValue; // They should set it to Unknown
                }
                // Special case for Level 8 (Null)
                else if (level.id === 8 && stock === null) {
                    if (value === 'null' && operator === '===') result = returnValue;
                    else result = 'Out of Stock'; // Default bad behavior
                }
                // Standard Logic Flow
                else if (operator === '===' && stock === Number(value)) result = returnValue;
                else if (operator === '==' && stock == Number(value)) result = returnValue;
                else if (operator === '>' && stock > Number(value)) result = returnValue;
                else if (operator === '<') {
                    const limit = value === 'threshold' ? 10 : Number(value);
                    if (stock < limit) result = returnValue;
                    else result = 'In Stock';
                }
                else result = 'In Stock'; // Default fallback

                // Check Correctness Logic
                let passed = false;
                if (level.id === 1) passed = (stock === 0 && result === 'Out of Stock') || (stock > 0 && result !== 'Out of Stock');
                else if (level.id === 2) passed = (stock < 10 && result === 'Low Stock') || (stock >= 10 && result !== 'Low Stock');
                else if (level.id === 3) passed = result === 'In Stock';
                else if (level.id === 4) passed = (stock < 10 && result === 'Low Stock') || (stock >= 10 && result !== 'Low Stock'); // Exact 10 should be In Stock
                else if (level.id === 5) passed = (stock < 0 && result === 'Unknown');
                else if (level.id === 6) passed = (stock > 100 && result === 'Overstock');
                else if (level.id === 7) passed = (stock === 0 && result === 'Out of Stock') && (String(stock) === '0' && result !== 'Out of Stock'); // Strict check
                else if (level.id === 8) passed = (stock === null && result === 'Unknown');
                else if (level.id === 9) passed = (stock < 20 && result === 'Low Stock'); // Flash sale
                else if (level.id === 10) passed = (stock === 0 && result === 'Out of Stock') || (stock > 0 && result === 'In Stock');

                if (!passed) allPassed = false;

                setTestResults(prev => [...prev, { stock, result, passed }]);

                // End of tests
                if (i === level.stockCases.length - 1) {
                    setIsRunning(false);
                    if (allPassed) {
                        setFeedback("🎉 Fix Deployed! System is stable.");
                        setTimeout(() => {
                            if (currentLevelIdx < LEVELS.length - 1) {
                                setCurrentLevelIdx(prev => prev + 1);
                            } else {
                                setCompleted(true);
                                completeModule('inventory-logic', 100, 'badge_logic');
                            }
                        }, 1500);
                    } else {
                        setFeedback("❌ Tests Failed. The bugs continue!");
                    }
                }
            }, i * 1000);
        });
    };

    return (
        <ModuleLayout
            moduleId="inventory-logic"
            title={`Smart Shelf Debugger (Level ${currentLevelIdx + 1}/10)`}
            description="Fix the broken inventory logic to save the store!"
        >
            <div
                className="min-h-full font-sans p-8 text-white relative"
                style={{
                    background: `linear-gradient(to bottom, rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.95)), url('/assets/smart-shelf-bg.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed'
                }}
            >
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* LEFT: Context & Output */}
                    <div className="space-y-6">
                        {/* Ticket Card */}
                        <div className="bg-white/95 backdrop-blur-sm text-slate-900 rounded-3xl p-6 shadow-xl border-l-8 border-red-500 relative overflow-hidden">
                            <div className="flex justify-between items-start mb-4">
                                <span className="bg-red-100 text-red-600 font-bold px-3 py-1 rounded-full text-xs uppercase tracking-wide">
                                    Bug Ticket #{level.id}
                                </span>
                                <span className="text-slate-400 font-mono text-sm">Created: Just now</span>
                            </div>
                            <h2 className="text-2xl font-black mb-2">{level.title}</h2>
                            <p className="text-slate-600 font-medium mb-4">{level.description}</p>

                            <div className="bg-red-50 p-4 rounded-xl border border-red-100 flex gap-3 items-start">
                                <Bug className="text-red-500 shrink-0 mt-1" />
                                <div>
                                    <h4 className="font-bold text-red-800 text-sm">Customer Report:</h4>
                                    <p className="text-red-700 italic">"{level.customerComplaint}"</p>
                                </div>
                            </div>
                        </div>

                        {/* Console Output */}
                        <div className="bg-black/90 backdrop-blur-md rounded-3xl p-6 shadow-2xl font-mono text-sm border border-slate-700 min-h-[300px]">
                            <div className="flex items-center gap-2 text-slate-500 border-b border-slate-800 pb-3 mb-3">
                                <Play size={14} /> Result Console
                            </div>
                            <div className="space-y-2">
                                {testResults.length === 0 && !isRunning && (
                                    <span className="text-slate-500 opacity-50">Waiting for test run...</span>
                                )}
                                {testResults.map((res, i) => (
                                    <motion.div
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        key={i}
                                        className="flex items-center gap-3"
                                    >
                                        <span className="text-blue-400">Stock: {String(res.stock)}</span>
                                        <span className="text-slate-500">→</span>
                                        <span className={`${res.passed ? 'text-green-400' : 'text-red-400'} font-bold`}>
                                            "{res.result}"
                                        </span>
                                        {res.passed ? <CheckCircle size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                                    </motion.div>
                                ))}
                                {feedback && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className={`mt-4 p-3 rounded-lg font-bold text-center ${feedback.includes('Fix') ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                                    >
                                        {feedback}
                                    </motion.div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: Code Editor */}
                    <div className="bg-slate-800/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-slate-700 flex flex-col relative">
                        <div className="absolute top-4 right-4 bg-slate-700 px-3 py-1 rounded text-xs font-mono text-slate-300">
                            inventory_logic.js
                        </div>

                        <div className="font-mono text-lg leading-relaxed flex-1">
                            <div className="text-slate-500 mb-4">function checkReorder(stock, threshold) {'{'}</div>

                            {/* Logic Block 1 */}
                            <div className={`pl-6 p-4 rounded-xl transition-all ${level.id === 1 || level.id === 6 || level.id === 7 || level.id === 8 ? 'bg-blue-500/10 border border-blue-500/50' : 'opacity-40 hover:opacity-100 transition-opacity'}`}>
                                <span className="text-purple-400">if</span> (stock
                                {true ? (
                                    <select
                                        value={operator}
                                        onChange={(e) => setOperator(e.target.value as Operator)}
                                        className="mx-2 bg-slate-900 border border-blue-500 rounded px-2 text-yellow-400 cursor-pointer hover:bg-slate-800"
                                    >
                                        <option value=">">&gt;</option>
                                        <option value="<">&lt;</option>
                                        <option value="===">===</option>
                                        <option value="!==">!==</option>
                                        <option value=">=">&gt;=</option>
                                        <option value="<=">&lt;=</option>
                                    </select>
                                ) : (
                                    <span className="text-yellow-400 mx-2">===</span>
                                )}
                                0) {'{'}
                                <div className="pl-6 mt-1">
                                    <span className="text-purple-400">return</span> <span className="text-green-400">"Out of Stock"</span>;
                                </div>
                                {'}'}
                            </div>

                            {/* Logic Block 2 */}
                            <div className={`pl-6 p-4 mt-2 rounded-xl transition-all ${level.id === 2 || level.id === 4 || level.id === 9 ? 'bg-blue-500/10 border border-blue-500/50' : 'opacity-40 hover:opacity-100 transition-opacity'}`}>
                                <span className="text-purple-400">else if</span> (stock &lt;
                                {true ? (
                                    <select
                                        value={value}
                                        onChange={(e) => setValue(e.target.value)}
                                        className="mx-2 bg-slate-900 border border-blue-500 rounded px-2 text-orange-400 cursor-pointer hover:bg-slate-800"
                                    >
                                        <option value={2}>2</option>
                                        <option value={5}>5</option>
                                        <option value={20}>20</option>
                                        <option value={100}>100</option>
                                        <option value="threshold">threshold</option>
                                        <option value="null">null</option>
                                    </select>
                                ) : (
                                    <span className="text-orange-400 mx-2">threshold</span>
                                )}
                                ) {'{'}
                                <div className="pl-6 mt-1">
                                    <span className="text-purple-400">return</span> <span className="text-green-400">"Low Stock"</span>;
                                </div>
                                {'}'}
                            </div>

                            {/* Logic Block 3 */}
                            <div className={`pl-6 p-4 mt-2 rounded-xl transition-all ${level.id === 3 || level.id === 5 || level.id === 10 ? 'bg-blue-500/10 border border-blue-500/50' : 'opacity-40 hover:opacity-100 transition-opacity'}`}>
                                <span className="text-purple-400">else</span> {'{'}
                                <div className="pl-6 mt-1">
                                    <span className="text-purple-400">return</span>
                                    {true ? (
                                        <select
                                            value={returnValue}
                                            onChange={(e) => setReturnValue(e.target.value as ReturnValue)}
                                            className="mx-2 bg-slate-900 border border-blue-500 rounded px-2 text-green-400 cursor-pointer hover:bg-slate-800"
                                        >
                                            <option value="Unknown">"Unknown"</option>
                                            <option value="Out of Stock">"Out of Stock"</option>
                                            <option value="In Stock">"In Stock"</option>
                                            <option value="Overstock">"Overstock"</option>
                                        </select>
                                    ) : (
                                        <span className="text-green-400 mx-2">"In Stock"</span>
                                    )};
                                </div>
                                {'}'}
                            </div>

                            <div className="text-slate-500 mt-4">{'}'}</div>
                        </div>

                        {/* Action Bar */}
                        <div className="mt-8 flex justify-end">
                            <button
                                onClick={runTests}
                                disabled={isRunning}
                                className={`
                                    px-8 py-4 rounded-xl font-bold flex items-center gap-3 text-lg transition-all shadow-lg
                                    ${isRunning
                                        ? 'bg-slate-700 text-slate-400 cursor-wait'
                                        : 'bg-green-500 hover:bg-green-400 text-white hover:scale-105 active:scale-95'
                                    }
                                `}
                            >
                                {isRunning ? <RotateCcw className="animate-spin" /> : <Play fill="currentColor" />}
                                {isRunning ? 'Running Tests...' : 'Deploy Fix'}
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {completed && (
                <ModuleSuccess
                    title="Logic Mastered!"
                    onRetry={() => {
                        setCompleted(false);
                        setCurrentLevelIdx(0);
                    }}
                >
                    <div className="space-y-4">
                        <p className="text-slate-600">
                            Awesome work! You fixed all the bugs. The warehouse system is now running perfectly.
                        </p>
                        <div className="bg-green-100 p-4 rounded-xl flex items-center gap-3">
                            <CheckCircle className="text-green-600" />
                            <span className="font-bold text-green-800">Warehouse Logic: STABLE</span>
                        </div>
                    </div>
                </ModuleSuccess>
            )}
        </ModuleLayout>
    );
};
